import {useState} from 'react';
import {Button, Card, Form, InputGroup} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {RESERVED_LOGINS, VpnAccount} from '../../Types/routerForms';
import {IP_REGEX, PASSWORD_REGEX, newLocalId, randomStrongPassword} from '../../Utils/random';
import Level3Title from '../../../../app/common/components/title/Level3Title';

interface AddVpnAccountFormProps {
  onAdd: (account: VpnAccount) => void;
  disabled?: boolean;
}

const LOGIN_REGEX = /^[a-zA-Z0-9._-]+$/;

const AddVpnAccountForm = ({onAdd, disabled}: AddVpnAccountFormProps): JSX.Element => {
  const intl = useIntl();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [remoteAddress, setRemoteAddress] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [validated, setValidated] = useState(false);

  const isLoginValid = LOGIN_REGEX.test(login) && !RESERVED_LOGINS.includes(login);
  const isPasswordValid = PASSWORD_REGEX.test(password);
  const isAddressValid = remoteAddress === '' || IP_REGEX.test(remoteAddress);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (!isLoginValid || !isPasswordValid || !isAddressValid) return;
    onAdd({
      id: newLocalId('vpn'),
      login,
      password,
      remoteAddress: remoteAddress || null,
    });
    setLogin('');
    setPassword('');
    setRemoteAddress('');
    setValidated(false);
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <Card.Title>
          <Level3Title title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.ADD_ACCOUNT'})} />
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label className="required">
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.LOGIN'})}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  isInvalid={validated && !isLoginValid}
                  required
                  disabled={disabled}
                />
                <Form.Control.Feedback type="invalid">
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.LOGIN_HELP'})}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label className="required">
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.PASSWORD'})}
                </Form.Label>
                <InputGroup hasValidation>
                  <Form.Control
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    isInvalid={validated && !isPasswordValid}
                    required
                    disabled={disabled}
                  />
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                  >
                    <i className={`fa-solid ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </Button>
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setPassword(randomStrongPassword(12))}
                  >
                    <i className="fa-solid fa-dice" />
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.PASSWORD'})}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.REMOTE_IP'})}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={remoteAddress}
                  onChange={(e) => setRemoteAddress(e.target.value)}
                  placeholder="(optionnel)"
                  isInvalid={validated && !isAddressValid}
                  disabled={disabled}
                />
                <Form.Control.Feedback type="invalid">
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.IP'})}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-2">
              <Button variant="primary" type="submit" disabled={disabled}>
                {intl.formatMessage({id: 'COMMON.ADD'})}
              </Button>
            </div>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AddVpnAccountForm;
